import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useState } from "react";
import { CalendarRange, Loader2, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { QueueInsight } from "@/components/QueueInsight";

const WINDOWS = [7, 30, 90];

export const QueueRollup = ({ onOpenAnalysis }) => {
  const [days, setDays] = usePersistedState("queue-rollup.days", 7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((d) => {
    setLoading(true);
    api.get(`/sip-trace/queue-rollup?days=${d}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const queues = data?.insight?.queues || [];
  return (
    <Card className="p-5" data-testid="sip-queue-rollup">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold text-slate-800">Queue patterns across traces</h2>
          {data && (
            <span className="text-[11px] text-slate-400">
              {data.analyses} analysis(es) in the last {data.days} days
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5">
          <CalendarRange className="ml-1.5 h-3.5 w-3.5 text-slate-400" />
          {WINDOWS.map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`rollup-window-${d}`}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                days === d ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-slate-400" data-testid="rollup-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Rolling up queue behaviour…
        </div>
      ) : queues.length === 0 ? (
        <p className="py-6 text-center text-xs text-slate-400" data-testid="rollup-empty">
          No queued calls found in analysed traces for this window.
        </p>
      ) : (
        <div className="space-y-4">
          <QueueInsight insight={data.insight} />
          {(data.worst_calls || []).length > 0 && (
            <div className="space-y-1.5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Worst offenders</h3>
              <div className="overflow-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs" data-testid="rollup-worst-table">
                  <thead className="bg-[#33456b] text-white">
                    <tr>
                      {["Queue", "Outcome", "Wait", "Trace", "Analysed", "By"].map((h) => (
                        <th key={h} className="px-2.5 py-1.5 text-left font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.worst_calls.map((w, i) => (
                      <tr key={i} onClick={() => onOpenAnalysis?.(w.analysis_id)}
                        data-testid={`rollup-worst-row-${i}`}
                        className={`cursor-pointer hover:bg-primary/5 ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
                        <td className="px-2.5 py-1.5 text-slate-700">{w.queue || "—"}</td>
                        <td className="px-2.5 py-1.5 font-semibold text-rose-600">{(w.outcome || "").replace(/_/g, " ")}</td>
                        <td className="px-2.5 py-1.5 text-slate-600">{w.wait_seconds ? `${w.wait_seconds}s` : "—"}</td>
                        <td className="px-2.5 py-1.5 text-slate-500">{w.file}</td>
                        <td className="px-2.5 py-1.5 text-slate-500">{(w.created_at || "").slice(0, 10)}</td>
                        <td className="px-2.5 py-1.5 text-slate-500">{w.created_by_name || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default QueueRollup;
